import React, { Fragment, useEffect, useRef } from 'react';

const renderPlanFeatures = (planName) => {
	switch (planName) {
		case 'essential':
			return (
				<Fragment>
					<div className="space-y-4 mb-3">
						You have successfully subscribed to the{' '}
						<p className="inline font-semibold">Essential Fantasy Fitness</p> plan.
						<p className="text-gray-800">
							Please enjoy the following features with your subscription:
						</p>
					</div>
					<ul className="list-disc ml-5 text-gray-700 space-y-2">
						<li className="ml-4">
							Roster Sync™ - connect your Yahoo, Sleeper, or FFPC rosters with Fantasy
							Fitness to gain valuable insights about your team
						</li>
						<li className="ml-4">
							Add up to 5 rosters - featuring advanced roster metrics, and the ability
							to add roster notes, player-specific notes, and more
						</li>
						<li className="ml-4">
							Collaborate with co-managers - seamlessly share and manage roster access
						</li>
						<li className="ml-4">
							College Prospect Profiles - complete with Prospect Score™, qualitative
							analysis, stats, videos and more
						</li>
						<li className="ml-4">
							Side-by-Side Compare Tool™ - compare up to five college prospects, NFL
							players or teams side-by-side
						</li>
						<li className="ml-4">
							Playoff Opponent Lists™ - strategize team stacks with each player's Week
							15, 16, 17 matchups
						</li>
						<li className="ml-4">Exclusive access to our premium Discord channels</li>
						<li className="ml-4">Ad-free experience</li>
					</ul>
				</Fragment>
			);
		case 'advanced':
			return (
				<Fragment>
					<div className="space-y-4 mb-3">
						You have successfully subscribed to the{' '}
						<p className="inline font-semibold">Advanced Fantasy Fitness</p> plan.
						<p className="text-gray-800">
							Please enjoy the following features with your subscription:
						</p>
					</div>
					<ul className="list-disc ml-5 text-gray-700 space-y-2">
						<li className="ml-4">
							Roster Sync™ - connect your Yahoo, Sleeper, or FFPC rosters with Fantasy
							Fitness to gain valuable insights about your team
						</li>
						<li className="ml-4">
							Add up to 20 rosters - featuring advanced roster metrics, and the ability
							to add roster notes, player-specific notes, and more
						</li>
						<li className="ml-4">
							Collaborate with co-managers - seamlessly share and manage roster access
						</li>
						<li className="ml-4">
							AI Insights - Player, team and personalized AI insights and recommendations
							for your rosters
						</li>
						<li className="ml-4">
							College Prospect Profiles - complete with Prospect Score™, qualitative
							analysis, stats, videos and more
						</li>
						<li className="ml-4">
							Side-by-Side Compare Tool™ - compare up to five college prospects, NFL
							players or teams side-by-side
						</li>
						<li className="ml-4">
							Playoff Opponent Lists™ - strategize team stacks with each player's Week
							15, 16, 17 matchups
						</li>
						<li className="ml-4">
							Draft Tracker with Roster Builder™ - your cutting-edge draft room companion
						</li>
						<li className="ml-4">
							Players Exposures Overview™ - instantly track your players exposures across
							platforms, formats, and scoring types
						</li>
						<li className="ml-4">Exclusive access to our premium Discord channels</li>
						<li className="ml-4">Ad-free experience</li>
					</ul>
				</Fragment>
			);
		case 'ultimate':
			return (
				<Fragment>
					<div className="space-y-4 mb-3">
						You have successfully subscribed to the{' '}
						<p className="inline font-semibold">Ultimate Fantasy Fitness</p> plan.
						<p className="text-gray-800">
							Please enjoy the following features with your subscription:
						</p>
					</div>
					<ul className="list-disc ml-5 text-gray-700 space-y-2">
						<li className="ml-4">
							Roster Sync™ - connect your Yahoo, Sleeper, or FFPC rosters with Fantasy
							Fitness to gain valuable insights about your team
						</li>
						<li className="ml-4">
							Add up to 100 rosters - featuring advanced roster metrics, and the ability
							to add roster notes, player-specific notes, and more
						</li>
						<li className="ml-4">
							Collaborate with co-managers - seamlessly share and manage roster access
						</li>
						<li className="ml-4">
							AI Insights - Player, team and personalized AI insights and recommendations
							for your rosters
						</li>
						<li className="ml-4">
							College Prospect Profiles - complete with Prospect Score™, qualitative
							analysis, stats, videos and more
						</li>
						<li className="ml-4">
							Side-by-Side Compare Tool™ - compare up to five college prospects, NFL
							players or teams side-by-side
						</li>
						<li className="ml-4">
							Playoff Opponent Lists™ - strategize team stacks with each player's Week
							15, 16, 17 matchups
						</li>
						<li className="ml-4">
							Draft Tracker with Roster Builder™ - your cutting-edge draft room companion
						</li>
						<li className="ml-4">
							Players Exposures Overview™ - instantly track your players exposures across
							platforms, formats, and scoring types
						</li>
						<li className="ml-4">Exclusive access to our premium Discord channels</li>
						<li className="ml-4">Ad-free experience</li>
					</ul>
				</Fragment>
			);
		default:
			return null;
	}
};

export default function SubscriptionSuccessModal({ isOpen, onClose, planName }) {
	const modalRef = useRef(null);

	useEffect(() => {
		if (!isOpen) return undefined;

		const handleClickOutside = (event) => {
			if (modalRef.current && !modalRef.current.contains(event.target)) {
				onClose();
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	return (
		<div
			className="fixed inset-0 flex justify-center items-center"
			style={{
				zIndex: 1000,
				backgroundColor: 'rgba(0, 0, 0, 0.6)'
			}}
		>
			<div
				ref={modalRef}
				className="relative bg-white rounded-lg p-8 shadow-2xl max-w-3xl w-full mx-4 md:mx-auto"
				style={{ maxHeight: '90vh', overflowY: 'auto' }}
			>
				<button
					className="absolute top-4 right-4 p-2 text-gray-600 hover:text-gray-900 transition-colors"
					onClick={onClose}
				>
					<svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth="2"
							d="M6 18L18 6M6 6l12 12"
						/>
					</svg>
				</button>
				<div className="flex items-center mb-6">
					<i className="fa-regular fa-circle-check text-green-500 text-2xl mr-2" />
					<h2 className="text-xl text-green-600 ">Success!</h2>
				</div>
				<div className="space-y-4 mb-4">
					<p className="text-gray-800">
						Welcome to Fantasy Fitness! We are excited to have you aboard.
					</p>
					<div className="text-gray-800">{renderPlanFeatures(planName)}</div>
					<p className="text-gray-800">
						If you have any questions or need assistance, please email{' '}
						<a
							href="mailto:matt@fantasyfitness.io?subject=Subscription%20Assistance"
							className="text-blue-600 underline hover:text-blue-800"
						>
							matt@fantasyfitness.io
						</a>
					</p>
					<p className="text-gray-800">We hope you enjoy your fantasy fitness journey!</p>
					<p className="text-gray-800">
						Sincerely, <br />
						Matt
					</p>
				</div>
			</div>
		</div>
	);
}
